export const scoreColor = (s) => s >= 72 ? "#00e676" : s >= 58 ? "#69f0ae" : s >= 44 ? "#ffd740" : s >= 30 ? "#ff9800" : "#ef5350";
/**
 * Setup grade.
 *
 * The server decides this, because the top tier depends on whether the setup
 * is actually tradeable — a sane stop, acceptable risk/reward, not already
 * extended — and not on the score alone. A stock can score well and still be
 * a bad entry, and "TOP SETUP" is the label most likely to end up in an advert,
 * so it has to mean something. This fallback only covers older cached scans
 * that predate the server-side grade.
 *
 * Crucially the fallback stops short of "TOP SETUP". Without the server's
 * verdict there is no way to know whether the tradeability gates passed, and
 * inferring the top tier from a high score is precisely the behaviour the
 * server-side grade was introduced to remove — it would hand the label back to
 * a stock that had been refused it.
 */
const TOP_TIER = "TOP SETUP";

export const gradeLabel = (stockOrScore) => {
  if (stockOrScore && typeof stockOrScore === "object") {
    if (stockOrScore.setupGrade) return stockOrScore.setupGrade;
    const inferred = gradeLabel(stockOrScore.score);
    return inferred === TOP_TIER ? "STRONG WATCH" : inferred;
  }
  const s = stockOrScore;
  return s >= 85 ? TOP_TIER : s >= 75 ? "STRONG WATCH" : s >= 65 ? "WATCH" : s >= 45 ? "NEUTRAL" : "WEAK";
};
/**
 * Alert rules come back in two shapes.
 *
 * The detail modal writes a bare price string ("210"), but the background
 * alert checker normalizes every rule into the smart-alert object and saves it
 * back — so within one check interval the same alert changes shape underneath
 * the UI. Read as a string that object rendered as "Alert at $[object Object]",
 * and parseFloat() on it returned NaN, so the client-side check silently
 * stopped firing. Both shapes are handled here rather than at each call site.
 */
export const alertTargets = (rule) => {
  if (rule === null || rule === undefined) return [];
  if (typeof rule === "number" || typeof rule === "string") {
    const n = Number(rule);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  if (typeof rule !== "object") return [];
  const out = [];
  if (Array.isArray(rule.customPrices)) {
    out.push(...rule.customPrices.map(Number).filter((n) => Number.isFinite(n) && n > 0));
  }
  const t = Number(rule.targetPrice);
  if (Number.isFinite(t) && t > 0) out.push(t);
  return out;
};

/** Human-readable summary of an alert rule, whichever shape it arrived in. */
export const alertLabel = (rule) => {
  const targets = alertTargets(rule);
  if (targets.length) return targets.map((n) => `$${n.toFixed(2)}`).join(", ");
  if (rule && typeof rule === "object" && rule.watchSetup) return "watching this setup";
  return "";
};

export const biasColor  = (b) => b === "BULLISH" ? "#00e676" : b === "BEARISH" ? "#ef5350" : "#ffd740";
export const fmt        = (n) => (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
export const fmtCD      = (s) => { if (!s) return ""; const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, "0")}`; };

/**
 * Missing-data formatters.
 *
 * Missing market data must never be rendered as a legitimate zero — "DAY LOW
 * $0.00" reads as a real price that the stock never traded at. These helpers
 * render N/A for anything that isn't a finite number, so an absent value is
 * always visibly absent. Note that 0 itself is a valid number and is preserved:
 * only null/undefined/NaN become N/A.
 */
export const NA = "N/A";

export const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/** Price → "$12.34", or "N/A". */
export const fmtPrice = (v, digits = 2) => (isNum(v) ? `$${v.toFixed(digits)}` : NA);

/** Percentage → "+1.23%" / "-1.23%", or "N/A". */
export const fmtPct = (v, digits = 2) =>
  isNum(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%` : NA;

/** Plain number → fixed decimals, or "N/A". */
export const fmtNum = (v, digits = 2) => (isNum(v) ? v.toFixed(digits) : NA);

/**
 * A cash amount → "$1,000.00", or "N/A".
 *
 * Separate from fmtPrice because these are position sizes and profits rather
 * than share prices, and read in the thousands where grouping matters.
 */
export const fmtMoney = (v, digits = 2) =>
  isNum(v)
    ? `$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
    : NA;

/**
 * A profit or loss → "+$127.40" / "-$63.10", or "N/A".
 *
 * The sign always leads, because the number is meaningless without it and a
 * red "-" that only appears on the colour is lost to anyone who can't see it.
 */
export const fmtMoneySigned = (v, digits = 2) =>
  isNum(v) ? `${v >= 0 ? "+" : "-"}${fmtMoney(v, digits)}` : NA;

/** Fractional share counts → "12.3456 shares", trimmed of trailing zeros. */
export const fmtShares = (v) => {
  if (!isNum(v)) return NA;
  const s = v.toFixed(4).replace(/\.?0+$/, "");
  return `${s} share${s === "1" ? "" : "s"}`;
};

/** Large currency amounts → "$13.27B" / "$412.5M", or "N/A". */
export const fmtBig = (v) => {
  if (!isNum(v)) return NA;
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3)  return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};

/** Date string → "Nov 3, 2026", or "N/A". */
export const fmtDate = (v) => {
  if (!v) return NA;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? NA
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Sanitizes free-typed decimal input for a price field. A plain `<input type="number">` silently
 * empties itself if the typed value doesn't match its strict period-decimal format — many locales
 * (including Hebrew/Israeli keyboards) type a comma as the decimal separator, so that keystroke
 * alone can make a price entry quietly submit nothing, on every device, every time. Using
 * type="text" + this sanitizer instead accepts either separator.
 */
export const sanitizeDecimalInput = (raw) => {
  const cleaned = String(raw).replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
};

/**
 * Rule-based keep/sell verdict for a tracked investment.
 * stock (optional) is the latest scanned data for the symbol. The predicted direction (tomorrowBias)
 * and trend/score drive the verdict more than the raw P&L, symmetrically in both directions:
 *  - up big but the scanner just turned bearish → SELL (lock in the gain before it reverses)
 *  - down a bit but the scanner still predicts a bounce → KEEP (don't panic-sell a dip in an uptrend)
 */
export function getInvestmentAdvice(entryPrice, currentPrice, stock) {
  const pnlPct = ((currentPrice - entryPrice) / entryPrice) * 100;
  const score  = stock?.score;
  const bias   = stock?.tomorrowBias;
  const trend  = stock?.trend;

  const isBearishSignal = bias === "BEARISH" || (score != null && score < 40) || (trend != null && trend < 40);
  const isBullishSignal = bias === "BULLISH" && score != null && score >= 60;

  // Deep loss — cut it as a risk-management floor, regardless of what the scanner predicts next.
  if (pnlPct <= -15) {
    return { verdict: "SELL", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% from your entry — consider cutting the loss.` };
  }

  if (isBearishSignal) {
    return pnlPct >= 0
      ? { verdict: "SELL", pnlPct, reason: `You're up ${pnlPct.toFixed(1)}%, but the scanner now reads bearish (score ${score ?? "—"}${bias ? `, predicted ${bias.toLowerCase()}` : ""}) — consider locking in gains before it slips further.` }
      : { verdict: "SELL", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% and the scanner reads bearish (score ${score ?? "—"}) — the setup that got you in isn't there anymore.` };
  }

  if (isBullishSignal) {
    return pnlPct < 0
      ? { verdict: "KEEP", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}%, but the scanner still predicts bullish (score ${score}) — could be a dip in an uptrend, not a reversal.` }
      : { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% and the scanner still predicts bullish (score ${score}) — momentum supports holding.` };
  }

  // No strong signal either way.
  if (pnlPct >= 30) {
    return { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% with no bearish signal — consider trimming some, but nothing forcing an exit yet.` };
  }
  if (pnlPct >= 0) {
    return { verdict: "KEEP", pnlPct, reason: `Up ${pnlPct.toFixed(1)}% with no strong signal either way — keep watching.` };
  }
  return { verdict: "KEEP", pnlPct, reason: `Down ${Math.abs(pnlPct).toFixed(1)}% with no strong signal either way — not enough to flag a sell yet.` };
}
